import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  Optional,
  Renderer2,
  Self
} from '@angular/core';
import { FormControlName, NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';
import { InputRootController } from '@shared/components/input/root/input-root-controller.directive';
import { InputType } from '@shared/components/input/root/input-type';

@Directive({
  selector: '[appInputFixedDigits]',
  providers: [{ provide: InputType, useExisting: InputFixedDigitsDirective }],
})
export class InputFixedDigitsDirective extends InputRootController implements AfterViewInit, OnDestroy {
  private sub?: Subscription;

  constructor(
    @Optional() @Self() @Inject(NgModel) protected override ngModel: NgModel,
    @Optional() @Self() @Inject(FormControlName) protected override formControl: FormControlName,
    protected override cdr: ChangeDetectorRef,
    protected override elementRef: ElementRef<HTMLInputElement>,
    private readonly _renderer: Renderer2
  ) {
    super(ngModel, formControl, cdr, elementRef);
  }

  public ngAfterViewInit(): void {
    // Ensure the native input is "text"
    this._renderer.setAttribute(this.elementRef.nativeElement, 'type', 'text');

    const ctrl = this.formControl?.control ?? this.ngModel?.control;

    // Normalize current value (covers patchValue/initial binding)
    const initial = ctrl?.value ?? this.elementRef.nativeElement.value;
    if (initial != null && initial !== '') {
      const normalized = this.normalizeFixedDigitsValue(initial);
      if (normalized !== initial) {
        ctrl?.setValue(normalized, { emitEvent: false });
      }
      this.setNativeValue(normalized ?? '');
    }

    // Normalize subsequent updates from the form control
    if (ctrl) {
      this.sub = ctrl.valueChanges.subscribe((v) => {
        const normalized = this.normalizeFixedDigitsValue(v);
        if (normalized !== v) {
          ctrl.setValue(normalized, { emitEvent: false });
        }
        if (this.elementRef.nativeElement.value !== (normalized ?? '')) {
          this.setNativeValue(normalized ?? '');
        }
      });
    }
  }

  public ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: Event): void {
    if (event instanceof KeyboardEvent) {
      // Allow: backspace, delete, tab, escape, enter, arrows, home, end
      const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

      if (allowedKeys.includes(event.key)) {
        return;
      }

      // Allow: Ctrl/Cmd+A, Ctrl/Cmd+C, Ctrl/Cmd+V, Ctrl/Cmd+X
      if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) {
        return;
      }

      // Allow only digits, comma, and dot
      if (!/^[0-9,.]$/.test(event.key)) {
        event.preventDefault();
        return;
      }

      // Prevent multiple dots or commas
      const currentValue = this.elementRef.nativeElement.value;
      if ((event.key === '.' || event.key === ',') && (currentValue.includes('.') || currentValue.includes(','))) {
        event.preventDefault();
      }
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') ?? '';
    const normalized = this.normalizeFixedDigitsValue(pastedText);

    if (normalized !== null) {
      const ctrl = this.formControl?.control ?? this.ngModel?.control;
      if (ctrl) {
        ctrl.setValue(normalized);
      } else {
        this.setNativeValue(normalized);
      }
    }
  }

  private setNativeValue(v: string): void {
    this.elementRef.nativeElement.value = v;
  }

  private normalizeFixedDigitsValue(value: unknown): string | null {
    if (value == null || value === '') return null;

    // Handle non-string primitives safely
    let str: string;
    if (typeof value === 'string') {
      str = value;
    } else if (typeof value === 'number') {
      str = value.toString();
    } else if (typeof value === 'boolean') {
      str = value.toString();
    } else {
      // For objects or other types, return null
      return null;
    }

    // Remove all characters except digits, comma, and dot
    let normalized = str.replaceAll(/[^0-9,.]/g, '');

    // Keep only the first dot or comma
    let hasSeparator = false;
    normalized = normalized.split('').filter(char => {
      if (char === '.' || char === ',') {
        if (hasSeparator) {
          return false;
        }
        hasSeparator = true;
        return true;
      }
      return true;
    }).join('');

    return normalized || null;
  }
}
